import React, { useState, useEffect} from 'react';
import timesheetLogo from '../../assets/images/timesheet-logo.png';
import { Link } from 'react-router-dom';
import './ForgotPassword.css';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { LoginNavbar } from '../../components/layout/Navbar';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
      document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  // Lock scroll and page height
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';

    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);

  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError(t("forgotPassword.promptEmail"));
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError(t("forgotPassword.invalidEmail"));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await axios.post(
        `${API_BASE_URL}user/forgot-password`,
        null,
        {
          params: {
            email: email
          }
        }
      );
      setIsSubmitted(true);
    } catch (err) {
      if (err.response.status === 404){
        setError(t("forgotPassword.emailError404"));
      } else {
        setError(t("forgotPassword.emailError"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = () => {
    setIsSubmitted(false);
    setEmail('');
    setError('');
  };

  if (isSubmitted) {
    return (
      <>
        <LoginNavbar />
        <div className="forgot-password-container">
          <div className="forgot-password-card">
            <div className="forgot-password-header">
              <img 
                src={timesheetLogo}
                alt="EZ Timesheet Logo" 
                className="forgot-password-logo"
              />
              <h1 className="forgot-password-title">{t("forgotPassword.successTitle")}</h1>
            </div>

            <div className="forgot-password-content">
              <div className="success-message">
                <div className="success-icon">✓</div>
                <p className="success-text">
                  {t("forgotPassword.successText1")} <strong>{email}</strong> {t("forgotPassword.successText2")}
                </p>
                <p className="success-subtext">
                  {t("forgotPassword.successSubtext")}
                </p>
              </div>

              <div className="forgot-password-actions">
                <button 
                  onClick={handleResendEmail}
                  className="resend-button"
                >
                  {t("forgotPassword.resendButton")}
                </button>
                <Link to="/login" className="back-to-login">
                  {t("forgotPassword.backToLogin")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LoginNavbar />
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          <div className="forgot-password-header">
            <img 
              src={timesheetLogo}
              alt="EZ Timesheet Logo" 
              className="forgot-password-logo"
            />
            <h1 className="forgot-password-title">{t("forgotPassword.defaultTitle")}</h1>
            <p className="forgot-password-subtitle">
              {t("forgotPassword.defaultSubtitle")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="forgot-password-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                {t("forgotPassword.emailAddress")}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`form-input ${error ? 'error' : ''}`}
                placeholder={t("forgotPassword.promptEmail")}
                disabled={isLoading}
              />
              {error && <span className="error-message">{error}</span>}
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="loading-spinner"></span>
                  {t("forgotPassword.sending")}
                </>
              ) : (
                t("forgotPassword.sendResetLink")
              )}
            </button>
          </form>

          <div className="forgot-password-footer">
            <p>
              {t("forgotPassword.rememberYourPassword")} <Link to="/login" className="login-link">{t("forgotPassword.signIn")}</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
